import { UsageService } from '../../../../core/usage/usage.service';
import { PatientService } from 'src/app/core/services/patient.service';
import { Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import * as _ from 'lodash';
import * as moment from 'moment';
import { takeUntil } from 'rxjs';
import { BaseComponent } from './../../../../core/components/base/base.component';
import { FeedbackService } from './../../../../core/services/feedback.service';
import { PatientBillingService } from './../../../../core/services/patient-billing.service';
import { BillingService } from 'src/app/core/services/billing.service';
import { FileDownloadService } from 'src/app/core/services/file-download.service';
import { GroupService } from 'src/app/core/services/group.service';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import { PatientAdmissionHistoryService } from 'src/app/core/services/patient-admission-history.service';
import { getCodeStateView } from 'src/app/shared/util/code-state.util';
import { doesCodeMatchStatus } from 'src/app/shared/util/code-status-filter.util';
import { formatRand } from 'src/app/shared/util/currency.util';
import { environment } from 'src/environments/environment';

interface CodingBillingSummary {
  schemeCents: number;      // scheme-approved portion (Σ connectApprovedCents)
  patientCents: number;     // patient-liable portion (Σ connectPatientLiableCents)
  paidCents: number;        // paid (Σ connectPaidCents)
  outstandingCents: number; // Σ max(billed − paid, 0)
  buckets: { current: number; d30: number; d60: number; d90: number; d120: number };
  hasData: boolean;
}

@Component({
  selector: 'app-patient-billing-list',
  templateUrl: './patient-billing-list.component.html',
  styleUrls: ['./patient-billing-list.component.scss']
})
export class PatientBillingListComponent extends BaseComponent implements OnInit {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);
  @Input() initialExpanded?: boolean;
  expanded?: boolean;
  @Output() expandedChange = new EventEmitter<boolean>();

  @ViewChild('startEndDateDialog') startEndDateDialog?: TemplateRef<any>;

  procedureCodes!: any[] | null;
  group: any = null;
  patient: any = null;
  admissionHistory: any[] = [];

  /** Connect-derived financial roll-up shown in the panel header. */
  billingSummary: CodingBillingSummary = this.emptyBillingSummary();
  formatCents = formatRand;

  private defaultStartDate = moment().startOf('month');
  private defaultEndDate = moment().endOf('month');
  exportOptions: any = { startDate: this.defaultStartDate, endDate: this.defaultEndDate }

  maxDate: moment.Moment = moment();

  isSubmitting = false;
  /** Confirm-button label for the shared start/end date dialog (reused by export + submit-to-Connect). */
  dateDialogConfirmLabel = 'Export';

  // Status filter properties (mirrors billing-view.component.ts)
  private static readonly BASE_STATUS_FILTERS = [
    { value: 'inpatient', label: 'Inpatient' },
    { value: 'opd', label: 'Outpatient' }
  ];
  statusFilters: string = '';
  availableStatusFilters = PatientBillingListComponent.BASE_STATUS_FILTERS;

  get connectFeatureEnabled(): boolean {
    return environment.connectEnabled;
  }

  get filteredProcedureCodes(): any[] | null {
    if (!this.procedureCodes) {
      return this.procedureCodes;
    }
    if (!this.statusFilters) {
      return this.procedureCodes;
    }
    return this.procedureCodes
      .map(group => ({
        ...group,
        codes: group.codes.filter((code: any) =>
          doesCodeMatchStatus(code, group.date, this.patient, this.statusFilters, undefined, this.admissionHistory)
        )
      }))
      .filter(group => group.codes.length > 0);
  }

  /** A state unit (tasks/state-mode.md) has no billing: no export, no money tooltips, no paid up front or pro bono labels. */
  get stateUnit(): boolean {
    return isStateModeGroup(this.groupService.group);
  }

  constructor(
    private router: Router,
    private groupService: GroupService,
    private patientBillingService: PatientBillingService,
    private billingService: BillingService,
    private patientService: PatientService,
    private fileDownloadService: FileDownloadService,
    private patientAdmissionHistoryService: PatientAdmissionHistoryService,
    fs: FeedbackService,
    md: MatDialog
  ) {
    super(fs, md);
  }

  ngOnInit(): void {
    this.patientBillingService.patientBilling$
      .pipe(takeUntil(this.destroy$))
      .subscribe((billing: any[] | null) => {
        this.procedureCodes = this.groupAndSortBilling(billing);
        this.billingSummary = this.computeBillingSummary(this.procedureCodes);
      });

    this.patientService.currentPatient$
      .pipe(takeUntil(this.destroy$))
      .subscribe((patient: any) => {
        this.patient = patient;
      });

    this.patientAdmissionHistoryService.patientAdmissionRecords$
      .pipe(takeUntil(this.destroy$))
      .subscribe((records: any[] | null) => {
        this.admissionHistory = records || [];
      });

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe((group: any) => {
        this.group = group;

        if (this.connectFeatureEnabled && this.group?.connectEnabled && this.group?.connectTenantId) {
          this.availableStatusFilters = [
            ...PatientBillingListComponent.BASE_STATUS_FILTERS,
            { value: 'not_submitted', label: 'Not Submitted to Connect' },
            { value: 'submitted', label: 'Submitted to Connect' }
          ];
        } else {
          this.availableStatusFilters = PatientBillingListComponent.BASE_STATUS_FILTERS;
          if (this.statusFilters === 'not_submitted' || this.statusFilters === 'submitted') {
            this.statusFilters = '';
          }
        }
      });
  }

  toggleExpanded() {
    this.expanded = !this.expanded;
    this.expandedChange.emit(this.expanded);
  }

  private groupAndSortBilling(codes: any[] | null): any[] | null {
    if (!codes || !codes.length) {
      return codes;
    }

    const grouped = _.groupBy(codes, code => moment(code.date).format('YYYY-MM-DD'));

    return _.chain(grouped)
      .map((value, key) => {
        const combinedCodes = value.map(item => ({
          id: item.id,
          code: item.code,
          description: item.description,
          paidCash: item.paidCash,
          noCharge: item.noCharge,
          provider: item.provider, // Include provider information
          status: item.status,
          source: item.source,
          connectClaimStatus: item.connectClaimStatus,
          connectSubmissionStatus: item.connectSubmissionStatus,
          connectLineStatus: item.connectLineStatus,
          connectBilledCents: item.connectBilledCents,
          connectApprovedCents: item.connectApprovedCents,
          connectRejectedCents: item.connectRejectedCents,
          connectPatientLiableCents: item.connectPatientLiableCents,
          connectPaidCents: item.connectPaidCents,
          connectClawbackCents: item.connectClawbackCents,
          connectClawbackAt: item.connectClawbackAt,
          connectClaimDate: item.connectClaimDate,
          connectProcedureCodeId: item.connectProcedureCodeId,
          state: getCodeStateView(item)
        }));
        return {
          date: key,
          codes: combinedCodes,
          codeList: combinedCodes.map(c => { return c.code + `${c.paidCash ? ' (Paid Up Front)' : ''}${c.noCharge ? ' (Pro Bono)' : ''}` }).join(', ')
        };
      })
      .orderBy(group => group.date, 'desc')
      .value();
  }

  private emptyBillingSummary(): CodingBillingSummary {
    return {
      schemeCents: 0, patientCents: 0, paidCents: 0, outstandingCents: 0,
      buckets: { current: 0, d30: 0, d60: 0, d90: 0, d120: 0 },
      hasData: false,
    };
  }

  /**
   * Roll up the Connect per-code ledger (billed/approved/patient-liable/paid,
   * pushed back via claim.line_financials) into the header summary. Outstanding
   * = billed − paid, aged per-code by the claim date (connectClaimDate, falling
   * back to service date) into 30/60/90/120+ buckets.
   */
  private computeBillingSummary(groups: any[] | null): CodingBillingSummary {
    const summary = this.emptyBillingSummary();
    if (!groups || !groups.length) {
      return summary;
    }

    const today = moment().startOf('day');
    for (const group of groups) {
      for (const code of group.codes) {
        const approved = Number(code.connectApprovedCents) || 0;
        const patient = Number(code.connectPatientLiableCents) || 0;
        const paid = Number(code.connectPaidCents) || 0;
        const billed = Number(code.connectBilledCents) || 0;

        if (code.connectBilledCents != null || code.connectApprovedCents != null ||
            code.connectPatientLiableCents != null || code.connectPaidCents != null) {
          summary.hasData = true;
        }

        summary.schemeCents += approved;
        summary.patientCents += patient;
        summary.paidCents += paid;

        const outstanding = billed - paid;
        if (outstanding > 0) {
          summary.outstandingCents += outstanding;
          // Age from the claim date (Connect's submittedAt) when known, so ageing
          // reflects submission rather than the visit date; fall back to the
          // service-date group when the claim date hasn't landed yet.
          const ageBasis = code.connectClaimDate ?? group.date;
          const ageDays = today.diff(moment(ageBasis).startOf('day'), 'days');
          if (ageDays <= 30) summary.buckets.current += outstanding;
          else if (ageDays <= 60) summary.buckets.d30 += outstanding;
          else if (ageDays <= 90) summary.buckets.d60 += outstanding;
          else if (ageDays <= 120) summary.buckets.d90 += outstanding;
          else summary.buckets.d120 += outstanding;
        }
      }
    }
    return summary;
  }

  loadCodeGroup(date: string, group: any) {
    if (this.patientService.isRecordingLocked()) {
      return;
    }
    this.router.navigate(['/patients', {
      outlets: {
        patient: ['edit-procedure-code', date],
      }
    }]);

  }

  getCodeTooltip(code: any): string {
    let tooltip = `${code.code} - ${code.description}`;
    
    if (code.paidCash) {
      tooltip += ' (Paid Up Front)';
    }
    
    if (code.noCharge) {
      tooltip += ' (Pro Bono)';
    }
    
    return tooltip;
  }

  async exportBilling(format: 'docx' | 'xlsx' = 'docx') {
    this.dateDialogConfirmLabel = 'Export';
    const ref = this.dialog.open(this.startEndDateDialog!, { autoFocus: false, disableClose: true });
    const dates = await ref.afterClosed().toPromise()
    if (!dates) return;

    this.billingService.exportBilling(this.group.id, this.exportOptions.startDate, this.exportOptions.endDate, [this.patient.id], undefined, format)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        async (blob: Blob) => {
          const fileExtension = format === 'xlsx' ? 'xlsx' : 'docx';
          await this.fileDownloadService.download(blob, `medicai_coding_export_${moment().valueOf()}.${fileExtension}`);
          this.showToastMessage('Coding exported successfully');
        },
        (error) => {
          console.error('Error exporting coding:', error);
          this.showToastMessage('There was an error exporting your coding. Please try again.');
        });
  }

  /** Submit this patient's coding to Connect for a chosen date range — mirrors exportBilling's date-dialog flow. */
  async submitToConnect() {
    if (!this.group?.connectEnabled || !this.group?.connectTenantId) {
      this.showToastMessage('This practice is not linked to Connect');
      return;
    }

    this.dateDialogConfirmLabel = 'Submit';
    const ref = this.dialog.open(this.startEndDateDialog!, { autoFocus: false, disableClose: true });
    const dates = await ref.afterClosed().toPromise();
    if (!dates) return;

    this.isSubmitting = true;
    // The service turns the picked calendar days into the stored UTC boundaries
    // (billingRangeStartIso). Converting here as well moved the start back a day.
    this.usage.flow('claim submission', 'start');
    this.billingService.submitBillingToSwitching(this.group.id, this.exportOptions.startDate, this.exportOptions.endDate, [this.patient.id], undefined)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: any) => {
          this.isSubmitting = false;
          if (res?.success) {
            this.showToastMessage('Coding submitted to Connect');
            this.usage.flow('claim submission', 'done');
          } else {
            this.showToastMessage('Failed to submit coding to Connect');
          }
        },
        error: (err: any) => {
          this.isSubmitting = false;
          const message = err?.error?.message;
          if (err?.status === 409) {
            this.showToastMessage(message || 'A submission for this date range is already pending.');
          } else {
            this.showToastMessage(message || 'There was an error submitting your coding to Connect. Please try again.');
          }
        }
      });
  }

}
